import { useTranslation } from 'react-i18next';
import { capitalize } from 'lodash-es';
import styled from '@emotion/styled';
import { tokens, Breadcrumbs, Text, Stack } from '@hotjar/hotsauce';
import { SETUP } from '@hotjar/webapp/constants/routes/overview';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { useCurrentSite } from '@hotjar/site';
import type { Site } from '@hotjar/site';
import type { CMS as CMSTypes } from '../IntegrationGuides';
import { STEPS } from '../types';

const Header = styled(Stack)`
  margin-bottom: ${tokens.spacer.x3};
`;

const useHeaderText = (currentStep: STEPS, currentCms?: CMSTypes) => {
  const { t } = useTranslation();

  const HEADER_TEXT = {
    [STEPS.MANUAL_INSTALLATION]: {
      title: t('activation:setup-wizard.main-page.methods-header.manual.title'),
    },
    [STEPS.NPM]: {
      title: t('activation:setup-wizard.main-page.methods-header.npm.title'),
      subtitle: t('activation:setup-wizard.main-page.methods-header.npm.subtitle'),
    },
    [STEPS.PLATFORMS]: {
      title: t('activation:setup-wizard.main-page.methods-header.platforms.title'),
    },
    [STEPS.SHARE_TRACKING_CODE]: {
      title: t('activation:setup-wizard.main-page.methods-header.share.title'),
    },
    [STEPS.GTM]: {
      title: t('activation:setup-wizard.main-page.methods-header.gtm.title'),
    },
    [STEPS.CMS]: {
      title: t('activation:setup-wizard.main-page.methods-header.cms.title', {
        cms: currentCms ? capitalize(currentCms) : '',
      }),
      subtitle: t('activation:setup-wizard.main-page.methods-header.cms.subtitle'),
    },
  } as { [step in STEPS]: { title: string; subtitle: string } };

  return {
    title: HEADER_TEXT[currentStep]?.title,
    subtitle: HEADER_TEXT[currentStep]?.subtitle,
  };
};

interface StepHeaderProps {
  currentStep: STEPS;
  currentCms?: CMSTypes;
  onBackClick: () => void;
}

export const StepHeader = ({
  currentStep,
  currentCms,
  onBackClick,
}: StepHeaderProps): JSX.Element => {
  const { t } = useTranslation();
  const { data: currentSite } = useCurrentSite();
  const { title, subtitle } = useHeaderText(currentStep, currentCms);

  const getLinkUrl = (siteId: Site['id']) => {
    const setupPath = getRoute(SETUP.path, { siteId });
    const step = currentStep === STEPS.CMS ? STEPS.PLATFORMS : STEPS.INSTALLATION_METHOD;
    return `${setupPath}?step=${step}`;
  };

  const handleClick = (e: React.SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onBackClick();
  };

  return (
    <>
      {currentSite ? (
        <Breadcrumbs>
          <Breadcrumbs.Item href={getLinkUrl(currentSite.id)} onClick={handleClick}>
            {`< ${t('activation:setup-wizard.main-page.methods-header.back-label')}`}
          </Breadcrumbs.Item>
        </Breadcrumbs>
      ) : null}
      <Header direction="column">
        {title ? (
          <Text textType="header4" as="h1">
            {title}
          </Text>
        ) : null}
        {subtitle ? (
          <Text textColor="text_light" textType="body1">
            {subtitle}
          </Text>
        ) : null}
      </Header>
    </>
  );
};
